import { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import History from '@tiptap/extension-history';
import { LinkIcon, Link2OffIcon } from 'lucide-react';
import sanitizeHtml from 'sanitize-html';
import { Button, cn } from '@design-system';

interface CustomDisclosuresEditorProps {
  id: string;
  label: string;
  description?: string;
  value: string;
  onChange: (html: string) => void;
  className?: string;
}

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ['p', 'a'],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  transformTags: {
    a: sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' }, true),
  },
};

function sanitize(html: string): string {
  return sanitizeHtml(html, SANITIZE_OPTIONS);
}

function normalizeLinkUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return `mailto:${trimmed}`;
  return `https://${trimmed}`;
}

export function CustomDisclosuresEditor({
  id,
  label,
  description,
  value,
  onChange,
  className,
}: CustomDisclosuresEditorProps) {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        id,
        class:
          'min-h-28 rounded-b-md bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none [&_p]:my-0 [&_a]:text-primary [&_a]:underline',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const isEmpty = ed.getText().trim().length === 0;
      onChange(isEmpty ? '' : sanitize(html));
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming === current) return;
    if (incoming === '' && editor.getText().trim().length === 0) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, value]);

  const handleAddLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const input = window.prompt('Link URL', previous ?? 'https://');
    if (input === null) return;
    if (input === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const href = normalizeLinkUrl(input);
    if (!href) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  }, [editor]);

  const handleRemoveLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  const isLinkActive = editor?.isActive('link') ?? false;

  return (
    <div className={cn('mt-2', className)}>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-gray-950"
      >
        {label}
      </label>
      {description && <p className="mb-2 text-sm text-foreground">{description}</p>}
      <div className="rounded-md border overflow-hidden">
        <div className="flex items-center gap-1 border-b bg-gray-50 p-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddLink}
            className={cn(
              'px-2 h-6 rounded py-1 text-xs font-medium',
              {"bg-primary/7 text-primary": isLinkActive}
            )}
            aria-label={isLinkActive ? 'Edit link' : 'Add link'}
          >
            <LinkIcon className="size-3.5" />
            {isLinkActive ? 'Edit link' : 'Add link'}
          </Button>
          {isLinkActive && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRemoveLink}
              className="px-2 h-6 bg-transparent rounded py-1 text-xs font-medium"
              aria-label="Remove link"
            >
              <Link2OffIcon className="size-3.5" />
              Remove
            </Button>
          )}
        </div>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
